/* eslint-disable react/prop-types */
import {useContext} from 'react'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import MoleculeAccordion, {moleculeAccordionBehavior, MoleculeAccordionItem} from '@s-ui/react-molecule-accordion'
import {useLocation, useRouter} from '@s-ui/react-router'
import CardAd from '@adv-mt/mt-card-ad'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import Layout from '@adv-mt/mt-layout-basic'
import LayoutPageContent from '@adv-mt/mt-layout-page-content'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import NavigationRequireAuth from '@adv-mt/mt-navigation-require-auth'
import IconAngleLeft from '@adv-mt/mt-svg-icons/lib/AngleLeft.js'
import IconChevronDown from '@adv-mt/mt-svg-icons/lib/ChevronDown.js'
import IconChevronUp from '@adv-mt/mt-svg-icons/lib/ChevronUp.js'
import IconInfo from '@adv-mt/mt-svg-icons/lib/InfoOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {ROUTES} from '../../../config/index.js'
import {getInitialProps} from './getInitialProps.js'

const RESERVATION_RETRIEVE_STEP_1 = 'Reservation Retrieve Step 1'

/* eslint-disable-next-line react/prop-types */
const SideBar = ({ad = {}, adFinancingData = {}}) => {
  const {i18n} = useContext(Context)

  const isDesktop = useMediaQuery(useMediaQueryBreakpoints.md)

  return isDesktop ? (
    <CardAd ad={ad} financingData={adFinancingData} vertical showFavorite={false} />
  ) : (
    <MoleculeAccordion
      behavior={moleculeAccordionBehavior.MULTIPLE}
      headerIconCollapsed={<IconChevronDown size="medium" />}
      headerIconExpanded={<IconChevronUp size="medium" />}
      as="div"
    >
      <MoleculeAccordionItem
        content={
          <div className="mt-ReservationRefund-cardAdWrapper">
            <CardAd
              ad={ad}
              cardClass={`mt-ReservationRefund-cardAd--borderLess`}
              financingData={adFinancingData}
              vertical
              showFavorite={false}
            />
          </div>
        }
        headerAs="h3"
        label={i18n.t('COMMON.VEHICLE_DETAILS')}
      />
    </MoleculeAccordion>
  )
}

const HelpText = () => {
  const {i18n} = useContext(Context)

  return (
    <div className="mt-ReservationRefund-helpText">
      <IconInfo size="medium" />
      <div className="mt-ReservationRefund-helpTextInfo">
        {i18n.t('VEHICLE_RESERVATION.PAGE_REFUND_REASON.HELP_TEXT')}
      </div>
    </div>
  )
}

const ReservationRefund = ({ad, adId, adFinancingData, reservation, reservationId, __HTTP__ = {}}) => {
  const {redirectTo} = __HTTP__

  const {domain, i18n} = useContext(Context)
  const {pathname, search} = useLocation()
  const {push} = useRouter()
  const {track} = useAnalyticsTrack()

  const {createdAt, expirationDate} = reservation || {}

  const config = domain.get('config')
  const validatedDateReservation = useDateFormat(new Date(createdAt))
  const expirationDateReservation = useDateFormat(new Date(expirationDate))

  const handlerClickedReason = () => {
    track({
      event: RESERVATION_RETRIEVE_STEP_1,
      data: {
        products: [{id: adId, category: 'ads', price: 0, quantity: 0}]
      }
    })

    push(`${ROUTES.RESERVATION.REFUND_REASON}?reservationId=${reservationId}&adId=${adId}`)
  }

  if (redirectTo) {
    push(redirectTo)
    return <></>
  }

  const {META_ROBOTS_NO_INDEX_NO_FOLLOW} = config.get('seo')
  const helmet = {
    meta: [META_ROBOTS_NO_INDEX_NO_FOLLOW]
  }

  return (
    <NavigationRequireAuth currentPath={`${pathname}${search}`}>
      <>
        <Helmet {...helmet} />
        <Layout.FullWidth
          linkFactory={LinkFactory}
          isFooterless
          topbarProps={{
            customContent: <></>,
            mainNavigationItems: [],
            navButton: (
              <AtomButton
                color="primary"
                design={atomButtonDesigns.FLAT}
                leftIcon={<IconAngleLeft />}
                link
                onClick={() => window.history.back()}
                size={atomButtonSizes.LARGE}
                shape={atomButtonShapes.CIRCULAR}
              />
            ),
            showBrandIcon: true,
            showNavUserOnDesktop: false,
            showToggleNavOnMobile: false,
            title: i18n.t('VEHICLE_RESERVATION.PAGE_REFUND.TOPBAR_TITLE')
          }}
        >
          <Layout.Container isFullHeight isFullWidth isMarginless>
            <LayoutPageContent
              buttonsConfig={{
                primaryCta: {
                  color: atomButtonColors.PRIMARY,
                  design: atomButtonDesigns.SOLID,
                  handleOnClick: handlerClickedReason,
                  text: i18n.t('VEHICLE_RESERVATION.PAGE_REFUND.CTA')
                }
              }}
              helpBox={<SideBar ad={ad} adFinancingData={adFinancingData} />}
              subtitle={i18n.t('VEHICLE_RESERVATION.PAGE_REFUND.SUBTITLE')}
              stepperConfig={{
                steps: 2,
                step: 1,
                labels: [i18n.t('VEHICLE_RESERVATION.PAGE_REFUND.STEPPER_LABEL'), '']
              }}
            >
              <div className="mt-ReservationRefund-mainContent">
                <HelpText />
                <div className="mt-ReservationRefund-description">
                  <div className="mt-ReservationRefund-descriptionInfoDateValid">
                    <div className="mt-ReservationRefund-descriptionInfoDateValid-item">
                      <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.RESERVATION_FROM')}</div>
                      <div>
                        <strong>{validatedDateReservation?.numeric}</strong>
                      </div>
                    </div>
                    <div className="mt-ReservationRefund-descriptionInfoDateValid-item">
                      <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.TEXT_VALID')}</div>
                      <div>
                        <strong>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.SUBTITLE_VALID')}</strong>
                      </div>
                    </div>
                    <div className="mt-ReservationRefund-descriptionInfoDateValid-item">
                      <div>{i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.RESERVATION_UNTIL')}</div>
                      <div>
                        <strong>{`${expirationDateReservation?.numeric}`}</strong>
                      </div>
                    </div>
                    <div className="mt-ReservationRefund-descriptionInfoDateValid-disclamer">
                      {i18n.t('VEHICLE_RESERVATION.MODAL_RESERVATION_INFO.DISCLAIMER')}
                    </div>
                  </div>
                </div>
              </div>
            </LayoutPageContent>
          </Layout.Container>
        </Layout.FullWidth>
      </>
    </NavigationRequireAuth>
  )
}

ReservationRefund.displayName = 'ReservationRefund'

ReservationRefund.getInitialProps = getInitialProps

export default ReservationRefund
